import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Toaster } from "./components/ui/sonner";
import Navbar from "./components/site/Navbar";
import Footer from "./components/site/Footer";
import ProtectedRoute from "./components/site/ProtectedRoute";
import AdminShell from "./components/site/AdminShell";
import Home from "./pages/Home";
import Inventory from "./pages/Inventory";
import VehicleDetail from "./pages/VehicleDetail";
import Finance from "./pages/Finance";
import TradeIn from "./pages/TradeIn";
import Contact from "./pages/Contact";
import About from "./pages/About";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import AdminVehicleForm from "./pages/AdminVehicleForm";
import AdminInbox from "./pages/AdminInbox";
import AdminTrash from "./pages/AdminTrash";
import AdminSettings from "./pages/AdminSettings";
import { AuthProvider } from "./lib/auth";
import { SettingsProvider } from "./lib/settings";

const pageVariants = {
  initial: { opacity: 0, y: 20, filter: "blur(8px)" },
  in: { opacity: 1, y: 0, filter: "blur(0px)" },
  out: { opacity: 0, y: -20, filter: "blur(8px)" },
};

const pageTransition = {
  duration: 0.7,
  ease: [0.76, 0, 0.24, 1],
};

/**
 * Liquid page overlay: full-viewport SVG blob that sweeps in and out
 * during route changes. Uses two colored layers with staggered timing
 * to feel viscous.
 */
function LiquidOverlay() {
  return (
    <>
      <motion.div
        className="fixed inset-0 z-[90] pointer-events-none origin-top"
        style={{
          background: "#050505",
          clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 100%)",
        }}
        initial={{ scaleY: 1 }}
        animate={{ scaleY: 0 }}
        exit={{ scaleY: 1 }}
        transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
      />
      <motion.div
        className="fixed inset-0 z-[91] pointer-events-none origin-top"
        style={{
          background: "#FF3B30",
          clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 100%)",
        }}
        initial={{ scaleY: 1 }}
        animate={{ scaleY: 0 }}
        exit={{ scaleY: 1 }}
        transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1], delay: 0.06 }}
      />
      {/* Liquid wave edge */}
      <motion.svg
        className="fixed left-0 right-0 z-[92] pointer-events-none"
        style={{ top: 0, height: "12vh", width: "100%" }}
        viewBox="0 0 100 20"
        preserveAspectRatio="none"
        initial={{ y: 0, opacity: 1 }}
        animate={{ y: "-100%", opacity: 0 }}
        exit={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
      >
        <path d="M0,10 Q25,20 50,10 T100,10 L100,0 L0,0 Z" fill="#FF3B30" />
      </motion.svg>
    </>
  );
}

function AnimatedRoutes() {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <React.Fragment key={location.pathname}>
        <LiquidOverlay />
        <motion.div
          variants={pageVariants}
          initial="initial"
          animate="in"
          exit="out"
          transition={pageTransition}
        >
          <Routes location={location}>
            <Route path="/" element={<Home />} />
            <Route path="/inventory" element={<Inventory />} />
            <Route path="/vehicle/:id" element={<VehicleDetail />} />
            <Route path="/finance" element={<Finance />} />
            <Route path="/trade-in" element={<TradeIn />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/vehicles/new" element={<ProtectedRoute><AdminVehicleForm /></ProtectedRoute>} />
            <Route path="/admin/vehicles/edit/:id" element={<ProtectedRoute><AdminVehicleForm /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute><AdminShell /></ProtectedRoute>}>
              <Route index element={<AdminDashboard />} />
              <Route path="messages" element={<AdminInbox kind="messages" />} />
              <Route path="test-drives" element={<AdminInbox kind="test-drives" />} />
              <Route path="trade-ins" element={<AdminInbox kind="trade-ins" />} />
              <Route path="trash" element={<AdminTrash />} />
              <Route path="settings" element={<AdminSettings />} />
            </Route>
          </Routes>
        </motion.div>
      </React.Fragment>
    </AnimatePresence>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

function App() {
  return (
    <div className="min-h-screen bg-[var(--bg)] text-white antialiased">
      <BrowserRouter>
        <AuthProvider>
          <SettingsProvider>
            <ScrollToTop />
            <Navbar />
            <main>
              <AnimatedRoutes />
            </main>
            <Footer />
            <Toaster theme="dark" position="bottom-right" />
          </SettingsProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
